import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Alert, Autocomplete, Avatar, Box, Button, Chip, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle, Divider, IconButton, MenuItem, TextField, Tooltip,
    Typography
} from "@mui/material";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import { toast } from "react-toastify";

import Langs, { Localize } from "../../app/lang/langs";
import NetworkManager from "../../app/managers/networkManager";
import { vlearnInputSx, vlearnSurface, vlearnType } from "./vlearnUi";
import {
    addVLearnManager, getVLearnCourse, getVLearnManagerCandidates, removeVLearnManager,
    transferVLearnOwnership
} from "../../containers/vlearn/vlearnSlice";

/**
 * V-Learning — who may edit this course.
 *
 * ── Two different permissions, and the difference matters ────────────────────────────
 *
 * Holding V-Learning **edit** access is what lets somebody create courses at all. Being
 * listed here is what lets them edit THIS one. The panel says so in as many words, because
 * the most common support question about a module built this way is "I have the permission,
 * why can I not open her course" — and the answer is that the two are not the same thing.
 *
 * That is also why the candidate list is short: `getManagerCandidates` only returns
 * collaborators who already hold `vlearn:edit` (plus administrators, who hold no permission
 * row because they bypass the check), and `addManager` re-checks the same rule against the
 * id it is given rather than trusting that it came from that list.
 */

/** See the identical note in CourseEditor: two thunk families, two error shapes. */
const serverMessage = (payload) => {
    const error = payload && payload.responseError
    if (!error) return null
    const data = error.data || (error.response && error.response.data)
    return (data && data.error) || null
}

const personLabel = (person) =>
    `${person.prenom || ''} ${person.nom || ''}`.trim() || person.email || `#${person.id}`

const initialsOf = (person) => {
    const first = String(person.prenom || '').trim()
    const last = String(person.nom || '').trim()
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || '?'
}

export default function CourseManagersPanel({ idCourse }) {

    const dispatch = useDispatch()

    const managers = useSelector((state) => state.vlearn.managers)
    const candidates = useSelector((state) => state.vlearn.managerCandidates)
    const access = useSelector((state) => state.vlearn.access)
    const reach = useSelector((state) => state.vlearn.reach)
    const lang = useSelector((state) => state.settings.lang)

    const [candidate, setCandidate] = useState(null)
    const [transferTo, setTransferTo] = useState('')
    const [confirmingTransfer, setConfirmingTransfer] = useState(false)
    const [busy, setBusy] = useState(false)

    const requested = useRef(null)
    useEffect(() => {
        if (!idCourse || requested.current === idCourse) return
        requested.current = idCourse
        dispatch(getVLearnManagerCandidates({ id_course: idCourse }))
    }, [dispatch, idCourse])

    /**
     * Both lists are re-read after every change.
     *
     * `managers` lives on the course response and `managerCandidates` is its complement — a
     * person added to one has to leave the other, and computing that locally is how the two
     * start disagreeing about who is already an instructor.
     */
    const reload = useCallback(() => {
        dispatch(getVLearnCourse({ id_course: idCourse }))
        dispatch(getVLearnManagerCandidates({ id_course: idCourse }))
    }, [dispatch, idCourse])

    const report = useCallback((response, successKey) => {
        const data = response.payload && response.payload.responseData
        if (data && data.status === 1) {
            toast.success(Localize(successKey, lang))
            return true
        }
        // `addManager` answers 422 with "This collaborator does not have edit access to
        // V-Learning", which is the one message that tells the author what to ask for.
        toast.error(serverMessage(response.payload) || Localize("VLEARN_ACTION_FAILED", lang))
        return false
    }, [lang])

    const canTransfer = !!(access && access.isOwner) || !!(reach && reach.editAll)

    const onAdd = async () => {
        if (!candidate) return
        setBusy(true)
        const response = await dispatch(addVLearnManager({
            id_course: idCourse, id_collab: candidate.id
        }))
        setBusy(false)
        if (report(response, "VLEARN_MANAGER_ADDED")) {
            setCandidate(null)
            reload()
        }
    }

    const onRemove = async (manager) => {
        setBusy(true)
        const response = await dispatch(removeVLearnManager({
            id_course: idCourse, id_collab: manager.id_collab
        }))
        setBusy(false)
        if (report(response, "VLEARN_MANAGER_REMOVED")) reload()
    }

    const onTransfer = async () => {
        setConfirmingTransfer(false)
        if (!transferTo) return
        setBusy(true)
        const response = await dispatch(transferVLearnOwnership({
            id_course: idCourse, id_collab: transferTo
        }))
        setBusy(false)
        if (report(response, "VLEARN_OWNERSHIP_TRANSFERRED")) {
            setTransferTo('')
            reload()
        }
    }

    const otherManagers = (managers || []).filter(manager => manager.role !== 'owner')

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            <Box sx={vlearnSurface.panel}>
                <Typography component="h2" sx={vlearnType.sectionTitle}>
                    <Langs str="VLEARN_INSTRUCTORS_TITLE" />
                </Typography>
                <Typography sx={{ ...vlearnType.body, mt: 0.5, maxWidth: 660 }}>
                    <Langs str="VLEARN_INSTRUCTORS_INTRO" />
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Box component="ul" sx={{
                    listStyle: 'none', margin: 0, padding: 0,
                    display: 'flex', flexDirection: 'column', gap: '8px'
                }}>
                    {(managers || []).map(manager => {
                        const isOwner = manager.role === 'owner'
                        const photoUrl = NetworkManager.getInstance()
                            .getCollabPhotoUrl(manager.id_collab, manager.photo_updated_at)
                        const removeLabel = Localize("VLEARN_REMOVE_MANAGER", lang)
                        return (
                            <Box component="li" key={manager.id} sx={{
                                display: 'flex', alignItems: 'center', gap: 1.5,
                                flexWrap: 'wrap',
                                padding: '10px 12px', borderRadius: '10px',
                                border: '1px solid rgba(0,0,0,0.07)'
                            }}>
                                {/* getCollabPhotoUrl answers null for a collab with no
                                    picture, so the initials are the state rather than a
                                    broken image request. */}
                                <Avatar src={photoUrl || undefined}
                                    sx={{ width: 34, height: 34, fontSize: '0.8rem' }}>
                                    {initialsOf(manager)}
                                </Avatar>
                                <Box sx={{ minWidth: 0, flex: '1 1 200px' }}>
                                    <Typography component="h3" sx={vlearnType.cardTitle}>
                                        {personLabel(manager)}
                                    </Typography>
                                    <Typography sx={vlearnType.meta}>{manager.email}</Typography>
                                </Box>
                                <Chip size="small"
                                    icon={isOwner
                                        ? <WorkspacePremiumOutlinedIcon sx={{ fontSize: 15 }} />
                                        : undefined}
                                    label={<Langs str={isOwner
                                        ? 'VLEARN_ROLE_OWNER' : 'VLEARN_ROLE_MANAGER'} />}
                                    sx={{
                                        fontSize: '0.72rem', fontWeight: 600,
                                        backgroundColor: isOwner
                                            ? 'rgba(255, 144, 21, 0.14)' : 'var(--lightGrey, #ededed)',
                                        color: isOwner ? '#B25E00' : '#3a3a3a'
                                    }} />

                                {/* The owner cannot be removed — `removeManager` refuses it,
                                    because a remove-then-add would leave the course
                                    ownerless in between. The tooltip names the operation
                                    that does work instead of leaving a dead button. */}
                                <Tooltip arrow title={isOwner
                                    ? Localize("VLEARN_OWNER_NOT_REMOVABLE", lang) : removeLabel}>
                                    <span>
                                        <IconButton size="small" disabled={isOwner || busy}
                                            onClick={() => onRemove(manager)}
                                            aria-label={removeLabel}
                                            sx={{
                                                '&:focus-visible': {
                                                    outline: '2px solid var(--orange, rgb(255,144,21))',
                                                    outlineOffset: '1px'
                                                }
                                            }}>
                                            <PersonRemoveOutlinedIcon sx={{ fontSize: 17 }} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        )
                    })}
                </Box>
            </Box>

            {/* ── Add ────────────────────────────────────────────────────── */}
            <Box sx={vlearnSurface.panel}>
                <Typography component="h2" sx={{ ...vlearnType.sectionTitle, mb: 0.5 }}>
                    <Langs str="VLEARN_ADD_INSTRUCTOR" />
                </Typography>
                <Typography sx={{ ...vlearnType.body, mb: 1.5, maxWidth: 660 }}>
                    <Langs str="VLEARN_ADD_INSTRUCTOR_HELP" />
                </Typography>

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <Autocomplete
                        sx={{ flex: '1 1 300px', minWidth: 0 }}
                        options={candidates || []}
                        value={candidate}
                        onChange={(event, value) => setCandidate(value)}
                        getOptionLabel={personLabel}
                        isOptionEqualToValue={(a, b) => Number(a.id) === Number(b.id)}
                        noOptionsText={Localize("VLEARN_NO_CANDIDATE", lang)}
                        renderOption={(props, person) => (
                            <li {...props} key={person.id}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                    <Avatar
                                        src={NetworkManager.getInstance().getCollabPhotoUrl(
                                            person.id, person.photo_updated_at) || undefined}
                                        sx={{ width: 26, height: 26, fontSize: '0.7rem' }}>
                                        {initialsOf(person)}
                                    </Avatar>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={{ fontSize: '0.85rem' }}>
                                            {personLabel(person)}
                                        </Typography>
                                        <Typography sx={vlearnType.meta}>{person.email}</Typography>
                                    </Box>
                                </Box>
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField {...params} size="small"
                                label={<Langs str="VLEARN_COLLABORATOR" />}
                                placeholder={Localize("VLEARN_ADD_INSTRUCTOR_PLACEHOLDER", lang)}
                                sx={vlearnInputSx} />
                        )}
                    />
                    <Button variant="contained" onClick={onAdd} disabled={!candidate || busy}
                        startIcon={<PersonAddAlt1OutlinedIcon sx={{ fontSize: 17 }} />}
                        sx={{
                            textTransform: 'none',
                            backgroundColor: 'var(--orange, rgb(255,144,21))',
                            '&:hover': { backgroundColor: '#e07f0d' }
                        }}>
                        <Langs str="VLEARN_ADD" />
                    </Button>
                </Box>
            </Box>

            {/* ── Ownership ──────────────────────────────────────────────── */}
            <Box sx={vlearnSurface.panel}>
                <Typography component="h2" sx={{ ...vlearnType.sectionTitle, mb: 0.5 }}>
                    <Langs str="VLEARN_TRANSFER_TITLE" />
                </Typography>
                <Typography sx={{ ...vlearnType.body, mb: 1.5, maxWidth: 660 }}>
                    <Langs str="VLEARN_TRANSFER_HELP" />
                </Typography>

                {canTransfer ? (
                    otherManagers.length ? (
                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            <TextField
                                select size="small" sx={{ flex: '1 1 260px', minWidth: 0 }}
                                label={<Langs str="VLEARN_NEW_OWNER" />}
                                value={transferTo}
                                onChange={(event) => setTransferTo(event.target.value)}
                            >
                                {otherManagers.map(manager => (
                                    <MenuItem key={manager.id} value={manager.id_collab}>
                                        {personLabel(manager)}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <Button variant="outlined" disabled={!transferTo || busy}
                                onClick={() => setConfirmingTransfer(true)}
                                sx={{ textTransform: 'none' }}>
                                <Langs str="VLEARN_TRANSFER" />
                            </Button>
                        </Box>
                    ) : (
                        // `transferOwnership` refuses an id that is not already a manager, so
                        // the honest instruction is to add the person first.
                        <Alert severity="info" sx={{ fontSize: '0.82rem' }}>
                            <Langs str="VLEARN_TRANSFER_NEEDS_MANAGER" />
                        </Alert>
                    )
                ) : (
                    <Alert severity="info" sx={{ fontSize: '0.82rem' }}>
                        <Langs str="VLEARN_TRANSFER_OWNER_ONLY" />
                    </Alert>
                )}
            </Box>

            <Dialog open={confirmingTransfer} onClose={() => setConfirmingTransfer(false)}
                aria-labelledby="vlearn-transfer-confirm">
                <DialogTitle id="vlearn-transfer-confirm"
                    sx={{ fontSize: '1.05rem', fontWeight: 700 }}>
                    <Langs str="VLEARN_TRANSFER_CONFIRM_TITLE" />
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={vlearnType.body}>
                        <Langs str="VLEARN_TRANSFER_CONFIRM_BODY" />
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmingTransfer(false)} sx={{ textTransform: 'none' }}>
                        <Langs str="VLEARN_CANCEL" />
                    </Button>
                    <Button onClick={onTransfer} variant="contained"
                        sx={{
                            textTransform: 'none',
                            backgroundColor: 'var(--orange, rgb(255,144,21))'
                        }}>
                        <Langs str="VLEARN_TRANSFER" />
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
